window.H7DeliveryGuarantee = window.H7DeliveryGuarantee || class H7DeliveryGuarantee extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;

    this.toggle = this.querySelector('[data-h7-delivery-guarantee-toggle]');
    this.status = this.querySelector('[data-h7-delivery-guarantee-status]');
    this.variantId = Number(this.dataset.variantId);
    this.onToggle = this.handleToggle.bind(this);
    this.onExternalUpdate = (event) => {
      if (this.isCartMutationInProgress()) return;
      const cart = event.detail?.cart;
      if (cart?.items) this.applyCart(cart);
      else this.sync();
    };
    this.onCartUpdate = (event = {}) => {
      if (this.isCartMutationInProgress()) return;
      if (event.cartData?.items) this.applyCart(event.cartData);
      else this.sync();
    };

    if (!this.toggle || !this.variantId) return;

    this.toggle.addEventListener('change', this.onToggle);
    window.addEventListener('h7:delivery-guarantee-updated', this.onExternalUpdate);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
    }
    this.dataset.ready = 'true';
    this.sync();
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('change', this.onToggle);
    window.removeEventListener('h7:delivery-guarantee-updated', this.onExternalUpdate);
    this.cartUpdateUnsubscriber?.();
    this.dataset.ready = 'false';
  }

  async handleToggle() {
    const shouldAdd = this.toggle.checked;
    this.beginCartMutation();
    this.setAllBusy(
      true,
      shouldAdd
        ? this.label('statusAdding', 'Adding porch theft protection')
        : this.label('statusRemoving', 'Removing porch theft protection')
    );

    try {
      const currentCart = await this.fetchCart(true);
      const currentLine = this.findGuaranteeLine(currentCart);
      let updatedCart = currentCart;

      if (shouldAdd && !currentLine) {
        const addPayload = await this.request(this.dataset.cartAddUrl, {
          items: [
            {
              id: this.variantId,
              quantity: 1,
              properties: { '_Hour Seven service': 'Porch Theft Protection' },
            },
          ],
        });
        const fallbackCart = this.withAddedItems(currentCart, addPayload);
        try {
          const fetchedCart = await this.fetchCart(true);
          updatedCart = this.findGuaranteeLine(fetchedCart) ? fetchedCart : fallbackCart;
        } catch (error) {
          updatedCart = fallbackCart;
        }
      } else if (!shouldAdd && currentLine) {
        const changePayload = await this.request(this.dataset.cartChangeUrl, { id: currentLine.key, quantity: 0 });
        updatedCart = changePayload?.items ? changePayload : await this.fetchCart(true);
      }

      this.broadcastCart(updatedCart);
      this.offerUndo(shouldAdd);
    } catch (error) {
      // Revert every rendered toggle, not just the one that was clicked: the
      // guarantee renders in the drawer, hover cart, notification and cart
      // footer at once, and the others were left showing the optimistic state.
      document.querySelectorAll('h7-delivery-guarantee').forEach((element) => {
        if (!element.toggle) return;
        element.toggle.checked = !shouldAdd;
        element.classList.toggle('is-added', !shouldAdd);
        element.dataset.added = (!shouldAdd).toString();
      });
      this.setStatus(this.label('statusError', 'We could not update porch theft protection. Please try again.'));
    } finally {
      this.endCartMutation();
      this.setAllBusy(false);
    }
  }

  async sync() {
    const cartVersion = this.cartVersion();
    try {
      const cart = await this.fetchCart();
      if (cartVersion !== this.cartVersion() || this.isCartMutationInProgress()) return;
      this.applyCart(cart);
    } catch (error) {
      this.setStatus(this.label('statusUnavailable', 'Porch theft protection is temporarily unavailable.'));
    }
  }

  applyCart(cart) {
    const line = this.findGuaranteeLine(cart);
    const hasEligibleItem = cart.items?.some((item) => Number(item.variant_id) !== this.variantId);

    this.hidden = !hasEligibleItem;
    const isAdded = Boolean(line && hasEligibleItem);
    this.toggle.checked = isAdded;
    this.classList.toggle('is-added', isAdded);
    this.dataset.added = isAdded ? 'true' : 'false';
    this.setStatus(
      isAdded
        ? this.label('statusAdded', 'Porch Theft Protection added.')
        : this.label('statusNotAdded', 'Porch Theft Protection not added.')
    );

    if (line && !hasEligibleItem) this.removeOrphanedGuarantee(line);
  }

  findGuaranteeLine(cart) {
    return cart.items?.find((item) => Number(item.variant_id) === this.variantId);
  }

  async request(url, body) {
    // Timeboxed so a stalled mutation can never leave the window-global
    // mutation flag set forever, which froze cart syncing on every instance.
    const response = await fetch(url, {
      ...fetchConfig(),
      body: JSON.stringify(body),
      signal: AbortSignal.timeout?.(10000),
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload.description || `Cart update failed: ${response.status}`);
    }

    return response.json();
  }

  fetchCart(force = false) {
    if (force || !window.__h7DeliveryGuaranteeCartPromise) {
      const promise = fetch(this.dataset.cartJsonUrl, {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: AbortSignal.timeout?.(10000),
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
          return response.json();
        })
        .finally(() => {
          if (window.__h7DeliveryGuaranteeCartPromise === promise) {
            window.__h7DeliveryGuaranteeCartPromise = null;
          }
        });

      window.__h7DeliveryGuaranteeCartPromise = promise;
    }

    return window.__h7DeliveryGuaranteeCartPromise;
  }

  cartVersion() {
    return Number(window.__h7DeliveryGuaranteeCartVersion || 0);
  }

  beginCartMutation() {
    window.__h7DeliveryGuaranteeCartVersion = this.cartVersion() + 1;
    window.__h7DeliveryGuaranteeCartMutationInProgress = true;
  }

  endCartMutation() {
    window.__h7DeliveryGuaranteeCartMutationInProgress = false;
  }

  isCartMutationInProgress() {
    return window.__h7DeliveryGuaranteeCartMutationInProgress === true;
  }

  updateAllInstances(cart) {
    document.querySelectorAll('h7-delivery-guarantee').forEach((element) => element.applyCart?.(cart));
  }

  broadcastCart(cart) {
    const resolvedCart = this.withResolvedTotal(cart);
    this.updateAllInstances(resolvedCart);
    this.refreshCartSurfaces(resolvedCart);
    window.dispatchEvent(new CustomEvent('h7:delivery-guarantee-updated', { detail: { cart: resolvedCart } }));
  }

  withResolvedTotal(cart) {
    if (!Array.isArray(cart?.items) || !cart.items.length) return cart;
    if (!cart.items.every((item) => Number.isFinite(Number(item.final_line_price)))) return cart;

    const resolvedCart = {
      ...cart,
      total_price: cart.items.reduce((total, item) => total + Number(item.final_line_price), 0),
      items_subtotal_price: cart.items.reduce((total, item) => total + Number(item.final_line_price), 0),
    };
    if (cart.items.every((item) => Number.isFinite(Number(item.original_line_price)))) {
      resolvedCart.original_total_price = cart.items.reduce(
        (total, item) => total + Number(item.original_line_price),
        0
      );
    }
    return resolvedCart;
  }

  withAddedItems(cart, addPayload) {
    const addedItems = Array.isArray(addPayload?.items)
      ? addPayload.items
      : addPayload?.variant_id
        ? [addPayload]
        : [];
    if (!addedItems.length) return cart;

    return this.withResolvedTotal({
      ...cart,
      item_count: Number(cart.item_count || 0) + addedItems.reduce((total, item) => total + Number(item.quantity || 0), 0),
      items: [...(cart.items || []), ...addedItems],
    });
  }

  removeOrphanedGuarantee(line) {
    if (window.__h7DeliveryGuaranteeCleanupPromise) return;

    const cleanup = this.request(this.dataset.cartChangeUrl, { id: line.key, quantity: 0 })
      .then((cart) => (cart?.items ? cart : this.fetchCart(true)))
      .then((cart) => this.broadcastCart(cart))
      .catch(() => this.setStatus(this.label('statusError', 'We could not update porch theft protection. Please try again.')))
      .finally(() => {
        if (window.__h7DeliveryGuaranteeCleanupPromise === cleanup) {
          window.__h7DeliveryGuaranteeCleanupPromise = null;
        }
      });

    window.__h7DeliveryGuaranteeCleanupPromise = cleanup;
  }

  refreshCartSurfaces(cart) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'h7-delivery-guarantee',
        cartData: cart,
        variantId: this.variantId,
      });
    }

    try {
      document.querySelector('cart-notification')?.render?.(cart);
    } catch (error) {
      // Keep the confirmed cart total updating even if the popup surface is stale.
    }

    this.updateDisplayedCartTotals(cart);
    this.refreshCartFooter(cart);
  }

  async refreshCartFooter(cart) {
    const current = document.querySelector('#main-cart-footer');
    if (!current) return;

    const sectionId = current.dataset.id;
    if (!sectionId) return;

    const source = await this.fetchSection(sectionId, '#main-cart-footer');
    if (!source) return;

    [
      '.h7-cart-summary__header',
      '.cart__blocks > .js-contents',
    ].forEach((selector) => {
      const currentContent = current.querySelector(selector);
      const sourceContent = source.querySelector(selector);
      if (currentContent && sourceContent) currentContent.innerHTML = sourceContent.innerHTML;
    });

    this.updateDisplayedCartTotals(cart);
  }

  updateDisplayedCartTotals(cart) {
    if (!cart?.currency) return;

    const compactTotal = this.formatMoney(cart.total_price, cart.currency);
    const formattedTotal = this.formatMoneyWithCurrency(cart.total_price, cart.currency);
    const formattedSubtotal = this.formatMoneyWithCurrency(
      cart.items_subtotal_price ?? cart.total_price,
      cart.currency
    );
    document
      .querySelectorAll('[data-h7-cart-total]')
      .forEach((element) => {
        element.textContent = compactTotal;
      });
    document
      .querySelectorAll('#main-cart-footer .totals__total-value, cart-drawer .totals__total-value')
      .forEach((element) => {
        element.textContent = formattedTotal;
      });
    document.querySelectorAll('[data-h7-cart-subtotal]').forEach((element) => {
      element.textContent = formattedSubtotal;
    });
  }

  formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency,
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `${(Number(cents || 0) / 100).toFixed(2)} ${currency}`;
    }
  }

  formatMoneyWithCurrency(cents, currency) {
    try {
      const formatted = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).format(Number(cents || 0) / 100);
      return formatted.includes(currency) ? formatted : `${formatted} ${currency}`;
    } catch (error) {
      return `${(Number(cents || 0) / 100).toFixed(2)} ${currency}`;
    }
  }

  async fetchSection(sectionId, requestKey) {
    if (!sectionId) return null;

    window.__h7DeliveryGuaranteeSectionRequests ||= new Map();
    window.__h7DeliveryGuaranteeSectionRequests.get(requestKey)?.abort();
    const controller = new AbortController();
    window.__h7DeliveryGuaranteeSectionRequests.set(requestKey, controller);

    try {
      const response = await fetch(
        `${routes.cart_url}?section_id=${encodeURIComponent(sectionId)}&h7_cart_version=${this.cartVersion()}`,
        {
          cache: 'no-store',
          signal: controller.signal,
        }
      );
      if (!response.ok) return null;
      const html = await response.text();
      if (window.__h7DeliveryGuaranteeSectionRequests.get(requestKey) !== controller) return null;
      return new DOMParser().parseFromString(html, 'text/html').querySelector(requestKey);
    } catch (error) {
      // The cart itself is already updated. A later cart render will refresh this optional surface.
      return null;
    } finally {
      if (window.__h7DeliveryGuaranteeSectionRequests.get(requestKey) === controller) {
        window.__h7DeliveryGuaranteeSectionRequests.delete(requestKey);
      }
    }
  }

  setBusy(busy, message = '') {
    this.toggle.disabled = busy;
    this.classList.toggle('is-loading', busy);
    if (message) this.setStatus(message);
  }

  setAllBusy(busy, message = '') {
    document.querySelectorAll('h7-delivery-guarantee').forEach((element) => element.setBusy?.(busy, message));
  }

  label(name, fallback) {
    return this.dataset[name] || fallback;
  }

  setStatus(message) {
    if (this.status) this.status.textContent = message;
  }

  offerUndo(wasAdded) {
    window.H7UndoToast?.show({
      message: wasAdded
        ? this.label('undoAdd', 'Porch Theft Protection added.')
        : this.label('undoRemove', 'Porch Theft Protection removed.'),
      actionLabel: this.label('undoAction', 'Undo'),
      onUndo: () => {
        this.toggle.checked = !wasAdded;
        this.handleToggle();
      },
    });
  }
}

if (!customElements.get('h7-delivery-guarantee')) {
  customElements.define('h7-delivery-guarantee', window.H7DeliveryGuarantee);
}
